/** Lookup → Customer page: tech-friendly mirror (no pricing). */
import { useCallback, useEffect, useState } from "react";
import { tr } from "@/i18n";
import { MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import { lookupClient } from "@/lib/api";
import { AccessNotesPanel } from "@/components/tech/AccessNotes";
import { Section, Empty, WORow, InspectionRow, ContactRow, Row } from "@/components/tech/lookup/LookupParts";

export default function LookupClient({ clientId, openSite, openWO, openInspection, openMaps }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async () => {
    try { const r = await lookupClient(clientId); setData(r.data); }
    catch { toast.error("Could not load customer"); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700" /></div>;

  const { client, sites, contacts, access_notes, work_orders, inspections, equipment } = data;
  const open = work_orders.filter(w => !["completed", "invoiced", "paid", "cancelled"].includes(w.status));
  const shown = filter === "open" ? open : filter === "done" ? work_orders.filter(w => !open.includes(w)) : work_orders;
  const targets = [{ target: "client", target_id: client.id, label: `Customer: ${client.name}` }, ...sites.map(s => ({ target: "site", target_id: s.id, label: `Site: ${s.name}` }))];

  return (
    <div className="space-y-2" data-testid="lookup-client-page">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-start gap-2">
          <Building2 className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-slate-900 leading-tight break-words" data-testid="lookup-client-name">{client.name}</h2>
            {client.facility_type && <div className="text-[10px] text-slate-500">{client.facility_type}</div>}
            {client.address_full && (
              <button type="button" onClick={() => openMaps(client.address_full)} className="text-[11px] text-blue-600 hover:underline text-left inline-flex items-start gap-1 mt-0.5">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />{client.address_full}
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
          <span><b className="text-slate-800">{work_orders.length}</b> work orders</span>
          <span><b className="text-slate-800">{open.length}</b> open</span>
          <span><b className="text-slate-800">{inspections.length}</b> inspections</span>
          <span><b className="text-slate-800">{sites.length}</b> sites</span>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
        <AccessNotesPanel notes={access_notes} targets={targets} onChanged={load} compact />
      </div>

      <Section title={tr("Sites")} count={sites.length} testId="lookup-client-sites">
        {sites.length === 0 && <Empty text="No sites on file." />}
        {sites.map(s => (
          <Row key={s.id} onClick={() => openSite(s.id)} testId={`lookup-site-${s.id}`}>
            <div className="flex items-center gap-1.5"><span className="text-xs font-semibold text-slate-800 truncate">{s.name}</span>{s.is_primary && <span className="text-[8px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">primary</span>}{s.access_note_count > 0 && <span className="text-[8px] text-amber-800 bg-amber-100 border border-amber-200 rounded px-1">{s.access_note_count} access note{s.access_note_count === 1 ? "" : "s"}</span>}</div>
            <div className="text-[10px] text-slate-500 truncate">{s.address_full || "No address"}</div>
          </Row>
        ))}
      </Section>

      <Section title={tr("Contacts")} count={contacts.length} testId="lookup-client-contacts">
        {contacts.length === 0 ? <Empty text="No contacts on file." /> : contacts.map((c, i) => <ContactRow key={c.id || i} c={c} />)}
      </Section>

      <Section title={tr("Work Orders")} count={shown.length} testId="lookup-client-wos" right={(
        <div className="flex gap-1">
          {[["all", "All"], ["open", "Open"], ["done", "Closed"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`lookup-wo-filter-${k}`}
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${filter === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      )}>
        {shown.length === 0 ? <Empty text="No work orders." /> : shown.map(w => <WORow key={w.id} wo={w} onOpen={openWO} />)}
      </Section>

      <Section title={tr("Inspections")} count={inspections.length} testId="lookup-client-inspections">
        {inspections.length === 0 ? <Empty text="No inspections yet." /> : inspections.map(i => <InspectionRow key={i.id} insp={i} onOpen={openInspection} />)}
      </Section>

      {equipment.length > 0 && (
        <Section title={tr("Equipment on record")} count={equipment.length} testId="lookup-client-equipment">
          <div className="flex flex-wrap gap-1">{equipment.map(e => <span key={e} className="text-[10px] rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-slate-700">{e}</span>)}</div>
        </Section>
      )}
    </div>
  );
}
